import { Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { Model } from '@df/catalog/model';
import { App } from '@df/core/app';
import { CacheService } from '@df/core/cache/cache';
import compact from 'lodash-es/compact';
import map from 'lodash-es/map';
import { ModelCarouselEngine } from './model-carousel-engine';
import { EModelCarouselVariant, IModelCarouselLoadRequest } from './model-carousel.interface';

@Injectable({
    providedIn: 'root'
})
export class ModelCarouselService {
    static engines: { [id: string]: ModelCarouselEngine } = {};

    constructor(
        protected cache: CacheService,
        protected app: App,
        protected requestService: RequestService
    ) {}

    /**
     * Unique cache ID getter
     */
    getUid(type: string, data: IModelCarouselLoadRequest) {
        const ids = type === 'product' ? compact(map(data.items, item => item.id)) : compact(data.ids);

        return [type, data.variant, data.category_id, ids?.join('-'), data.limit, data.verbosity].join('-');
    }

    /**
     * Load models method
     */
    async loadModels(type: string, data: IModelCarouselLoadRequest) {
        const cName = this.getUid(type, data);
        const cached = this.cache.get(cName);

        if (cached) {
            return cached;
        }

        let result: Model[] = [];
        const variant = data.variant;
        const engine = ModelCarouselService.engines[variant];

        if (engine && type !== EModelCarouselVariant.CategoryProducts) {
            result = await engine.get(type, data);
        } else {
            const resource = this.app.resources[type];
            const ids = type === 'product' ? compact(map(data.items, item => Number(item.id))) : compact(data.ids);
            const categoryId = data.category_id;

            switch (variant) {
                case EModelCarouselVariant.ModelsIds:
                    if (ids?.length) {
                        await resource.loadAllIds(ids, data.verbosity || 2, { safe: true });

                        result = resource.getAllExisting(ids);
                    }

                    break;
                case EModelCarouselVariant.CategoryProducts:
                    if (categoryId) {
                        result = await this.requestService.loadCategoryProducts({
                            verbosity: data.verbosity || 2,
                            limit: data.limit || 36,
                            category: categoryId
                        });
                    }

                    break;
            }
        }

        return this.cache.set(cName, result);
    }
}
